import { useMemo } from 'react';
import { BookingNotCancelableReasons, BookingStatus } from 'store/bookings/types';
import { TransactionDetails } from 'store/transactions/types';

import { MILLISECONDS_IN_MIN } from 'shared/consts/time';

export interface UseIsBookingCancelableResult {
  cancelable: boolean;
  notCancelableReason?: BookingNotCancelableReasons;
}

/**
 * RES-1174. Statuses that have already ended the booking, so there is nothing left to cancel.
 *
 * This hook keyed only on dates and the two allow-flags, never on status, which was harmless
 * for as long as none of these could reach a surface that offers cancelling. Rejected bookings
 * now do: they are future-dated by definition (the read is bounded on `end_at`), so every one of
 * them came out `cancelable: true` and every surface below offered a live Cancel that would have
 * fired a real cancel against an already-denied booking.
 *
 * Gated here rather than in each tile because four surfaces consume this: both booking tiles,
 * the redesigned booking-details page, the legacy full-screen cancel step, and the
 * booking-limit-reached modal. RES-1181 surfaces cancelled bookings and needs the same guard.
 */
const TERMINAL_STATUSES: Array<BookingStatus> = [
  BookingStatus.DENIED_AVAILABLE,
  BookingStatus.DENIED_UNAVAILABLE,
  BookingStatus.CANCELED,
  BookingStatus.LATE_CANCEL,
];

export default function useIsBookingCancelable(transaction: TransactionDetails): UseIsBookingCancelableResult {
  const isTerminal = TERMINAL_STATUSES.includes(transaction?.details?.status);

  const isCancellationPeriodExpired = useMemo(() => {
    const bookingStartAt = transaction?.details?.start_at;
    const disallowedMinutesBeforeStart = transaction?.details?.cancellation_window_end_minutes;
    if (!bookingStartAt || !disallowedMinutesBeforeStart) {
      return false;
    }
    const bookingStartAtDatetime = new Date(bookingStartAt);
    const now = new Date();
    const limitDatetime = new Date(
      bookingStartAtDatetime.getTime() - disallowedMinutesBeforeStart * MILLISECONDS_IN_MIN,
    );

    return now.getTime() > limitDatetime.getTime();
  }, [transaction]);

  const isBookingInThePast = useMemo(() => {
    return transaction?.details?.start_at && new Date() > new Date(transaction?.details?.start_at);
  }, [transaction]);

  const allowLateCancel = transaction?.details?.allow_late_cancel;
  const allowCancelDuring = transaction?.details?.allow_cancel_during;

  const notCancelableReason = useMemo(() => {
    // Both remaining reasons explain a TIMING rule the tenant could otherwise have satisfied, and
    // neither is true of a booking that has already ended by status. A rejected booking is
    // reachable here while its slot is still running -- the read is bounded on `end_at` -- so
    // without this it would report BOOKING_IN_THE_PAST and the disabled control would blame the
    // clock for a refusal.
    if (isTerminal) {
      return undefined;
    }
    if (isBookingInThePast && !allowCancelDuring) {
      return BookingNotCancelableReasons.BOOKING_IN_THE_PAST;
    }
    if (isCancellationPeriodExpired && !allowLateCancel && !isBookingInThePast) {
      return BookingNotCancelableReasons.CANCELATION_PERIOD_CLOSED;
    }
    if (isBookingInThePast && allowCancelDuring) {
      return undefined;
    }
    return undefined;
  }, [isTerminal, isCancellationPeriodExpired, isBookingInThePast, allowLateCancel, allowCancelDuring]);

  const cancelable = useMemo(() => {
    if (!transaction?.details) return false;

    // Deliberately no `notCancelableReason`: the existing two both explain a *timing* rule the
    // tenant could otherwise have satisfied, and neither is true here. Callers that render a
    // reason fall back to the plain disabled "Cancel booking" label, and the tile suppresses the
    // control outright.
    if (isTerminal) return false;

    // If booking is in the past and cancellation during booking is allowed
    if (isBookingInThePast && allowCancelDuring) {
      return true;
    }

    // If booking is in the past and cancellation during booking is NOT allowed
    if (isBookingInThePast) {
      return false;
    }

    // If cancellation period expired but late cancellations are allowed
    if (isCancellationPeriodExpired && allowLateCancel) {
      return true;
    }

    // If cancellation period NOT expired
    if (!isCancellationPeriodExpired) {
      return true;
    }

    return false;
  }, [transaction, isTerminal, isCancellationPeriodExpired, isBookingInThePast, allowLateCancel, allowCancelDuring]);

  return {
    cancelable,
    notCancelableReason,
  };
}
